import { useTranslation } from "react-i18next";
import {
  Paper,
  Typography,
  Skeleton,
  Box,
  alpha,
  useTheme,
  type SxProps,
  type Theme,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  formatTrendChartAxisDate,
  formatTrendChartTooltipPeriodLabel,
  formatViewerIntegerFloor,
  LIVESTREAM_METRIC_LABELS,
  explorerAccentAt,
} from "./utils";

export type LivestreamViewerDualLineChartData = Array<{
  weekStart: string;
  avgViewers: number;
  peakViewers: number;
}>;

export type LivestreamViewerDualLineChartBodyProps = {
  data: LivestreamViewerDualLineChartData;
  granularityTooltip: string;
  peakColor: string;
  avgColor: string;
  height: number;
  emphasis?: "avg" | "peak";
  onDayClick?: (date: string) => void;
  hoverAnimate?: boolean;
};

function dateFromLiveChartIndex(
  data: LivestreamViewerDualLineChartData,
  index: unknown,
): string | undefined {
  if (index == null || index === "") return undefined;
  const i = typeof index === "number" ? index : Number(index);
  if (!Number.isInteger(i) || i < 0) return undefined;
  const date = data[i]?.weekStart?.slice(0, 10);
  return date && /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : undefined;
}

function dateFromNearestTick(
  container: HTMLElement,
  clientX: number,
  data: LivestreamViewerDualLineChartData,
): string | undefined {
  const ticks = container.querySelectorAll(
    ".xAxis .recharts-cartesian-axis-tick",
  );
  let bestDist = Infinity;
  let bestIndex = -1;
  ticks.forEach((el, index) => {
    const box = el.getBoundingClientRect();
    const dist = Math.abs(clientX - (box.left + box.width / 2));
    if (dist < bestDist) {
      bestDist = dist;
      bestIndex = index;
    }
  });
  if (bestIndex < 0 || bestDist > 40) return undefined;
  return dateFromLiveChartIndex(data, bestIndex);
}

export const LivestreamViewerDualLineChartBody: React.FC<
  LivestreamViewerDualLineChartBodyProps
> = ({
  data,
  granularityTooltip,
  peakColor,
  avgColor,
  height,
  emphasis,
  onDayClick,
  hoverAnimate = true,
}) => {
  const animateHover = Boolean(onDayClick && hoverAnimate);
  const theme = useTheme();
  const peakOpacity = emphasis === "avg" ? 0.35 : 1;
  const avgOpacity = emphasis === "peak" ? 0.35 : 1;
  const peakWidth = emphasis === "peak" ? 2.5 : 2;
  const avgWidth = emphasis === "avg" ? 2.5 : 2;

  return (
    <Box
      sx={{
        width: "100%",
        height,
        pl: 0.75,
        cursor: onDayClick ? "pointer" : undefined,
        outline: "none",
        "&:focus, &:focus-visible, *:focus, *:focus-visible": {
          outline: "none",
        },
        overflow: "visible",
        "& .recharts-responsive-container, & .recharts-wrapper, & .recharts-surface": {
          outline: "none",
          overflow: "visible",
        },
        "& .recharts-tooltip-cursor": {
          display: "none",
        },
        ...(animateHover
          ? {
              "@keyframes chartDotPop": {
                "0%": { transform: "scale(0.45)", opacity: 0.55 },
                "100%": { transform: "scale(1)", opacity: 1 },
              },
              "& .recharts-active-dot": {
                transformBox: "fill-box",
                transformOrigin: "center",
                animation: "chartDotPop 180ms ease-out",
              },
              "& .recharts-dot": {
                transformBox: "fill-box",
                transformOrigin: "center",
                transition: "transform 160ms ease, opacity 160ms ease",
              },
            }
          : {}),
      }}
      onClick={
        onDayClick
          ? (event) => {
              const date = dateFromNearestTick(
                event.currentTarget,
                event.clientX,
                data,
              );
              if (date) onDayClick(date);
            }
          : undefined
      }
    >
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
        data={data}
        margin={{ top: 4, right: 8, bottom: 0, left: 8 }}
        onClick={(state) => {
          if (!onDayClick) return;
          const date = dateFromLiveChartIndex(
            data,
            state.activeIndex ?? state.activeTooltipIndex,
          );
          if (date) onDayClick(date);
        }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={alpha(theme.palette.divider, 0.15)}
          vertical={false}
        />
        <XAxis
          dataKey="weekStart"
          tickFormatter={formatTrendChartAxisDate}
          tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
          tickLine={false}
          axisLine={{ stroke: alpha(theme.palette.divider, 0.3) }}
        />
        <YAxis
          tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
          tickLine={false}
          axisLine={false}
          width={52}
          allowDecimals={false}
          tickFormatter={(v) => formatViewerIntegerFloor(v as number)}
        />
        <RechartsTooltip
          cursor={false}
          {...(onDayClick
            ? {
                position: { x: 46, y: 4 },
                wrapperStyle: { pointerEvents: "none" as const },
              }
            : {})}
          itemSorter={(item) =>
            item.dataKey === "peakViewers"
              ? 0
              : item.dataKey === "avgViewers"
                ? 1
                : 2
          }
          formatter={(value, name) => [
            formatViewerIntegerFloor(value as number),
            String(name),
          ]}
          labelFormatter={(label) =>
            formatTrendChartTooltipPeriodLabel(
              label as string,
              granularityTooltip,
            )
          }
          contentStyle={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 8,
            padding: "6px 10px",
            fontSize: 12,
          }}
        />
        <Legend
          formatter={(value, entry) => (
            <span style={{ color: entry.color }}>{String(value)}</span>
          )}
          wrapperStyle={{ fontSize: 12 }}
        />
        <Line
          type="monotone"
          dataKey="peakViewers"
          name="Peak Max"
          stroke={peakColor}
          strokeWidth={peakWidth}
          strokeOpacity={peakOpacity}
          dot={false}
          activeDot={
            onDayClick
              ? {
                  r: 6,
                  fill: peakColor,
                  stroke: theme.palette.background.paper,
                  strokeWidth: 2,
                }
              : { r: 4, fill: peakColor }
          }
        />
        <Line
          type="monotone"
          dataKey="avgViewers"
          name={LIVESTREAM_METRIC_LABELS.avgEsp}
          stroke={avgColor}
          strokeWidth={avgWidth}
          strokeOpacity={avgOpacity}
          dot={false}
          activeDot={
            onDayClick
              ? {
                  r: 6,
                  fill: avgColor,
                  stroke: theme.palette.background.paper,
                  strokeWidth: 2,
                }
              : { r: 4, fill: avgColor }
          }
        />
      </LineChart>
    </ResponsiveContainer>
    </Box>
  );
};

export type LivestreamViewerDualLineChartProps = {
  data: LivestreamViewerDualLineChartData;
  granularityTooltip: string;
  isLoading?: boolean;
  peakColor?: string;
  avgColor?: string;
  chartHeight?: number;
  sx?: SxProps<Theme>;
};

/** Third chart in Tendencias semanales: peak takes that slot's accent, average the next. */
const DEFAULT_PEAK_COLOR = explorerAccentAt(2);
const DEFAULT_AVG_COLOR = explorerAccentAt(3);

export const LivestreamViewerDualLineChart: React.FC<
  LivestreamViewerDualLineChartProps
> = ({
  data,
  granularityTooltip,
  isLoading = false,
  peakColor = DEFAULT_PEAK_COLOR,
  avgColor = DEFAULT_AVG_COLOR,
  chartHeight = 140,
  sx,
}) => {
  const { t } = useTranslation("explorer");

  return (
    <Paper
      elevation={0}
      sx={[
        {
          flex: 1,
          minWidth: { xs: "100%", md: 280 },
          p: 2,
          overflow: "hidden",
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Typography
        variant="body2"
        fontWeight={600}
        color="text.primary"
        sx={{ mb: 1.5 }}
      >
        {t("liveViewers")}
      </Typography>

      {isLoading ? (
        <Box sx={{ width: "100%", height: chartHeight }}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height="100%"
            animation={false}
            sx={{ borderRadius: 1 }}
          />
        </Box>
      ) : (
        <LivestreamViewerDualLineChartBody
          data={data}
          granularityTooltip={granularityTooltip}
          peakColor={peakColor}
          avgColor={avgColor}
          height={chartHeight}
        />
      )}
    </Paper>
  );
};
